import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { useConstructionPerformance, usePerformanceAlerts } from './hooks/usePerformance';
import { useAuth } from '@/core/auth/AuthContext';
import { KPICard, Card, Badge, Button, Table, Divider, Filter, Search, Alert } from '@/components/ui';

export default function PerformanceDashboardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { snapshots, liveKpis, isLoading, filters, setFilters } = useConstructionPerformance(companyId, { projectId });
  const { data: alerts } = usePerformanceAlerts(companyId, projectId || '');

  return (
    <PageLayout
      title="Hiệu suất thi công"
      description="Trung tâm phân tích năng suất, chất lượng và chi phí thi công thời gian thực."
      breadcrumbs={[{ label: 'Thi công', path: `/construction/${projectId}/plan` }, { label: 'Hiệu suất' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Xuất báo cáo</Button>
          <Button variant="primary" icon="ri-refresh-line">Cập nhật Snapshot</Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Alerts Section */}
        {alerts && alerts.length > 0 && (
          <div className="space-y-2">
            {alerts.map(alert => (
              <Alert
                key={alert.id}
                variant={alert.severity_code === 'critical' ? 'danger' : 'warning'}
                title={alert.title}
                description={alert.description}
                action={<Button size="xs" variant="ghost">Xử lý ngay</Button>}
              />
            ))}
          </div>
        )}

        {/* Live KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Lệnh thi công đang mở"
            value={liveKpis?.activeWorkOrders || 0}
            icon="ri-task-line"
            variant="primary"
          />
          <KPICard
            title="QA Pass Rate"
            value={liveKpis?.averageQaPassRate || 0}
            suffix="%"
            icon="ri-shield-check-line"
            variant="success"
          />
          <KPICard
            title="Biến động chi phí"
            value={liveKpis?.costVariance || 0}
            suffix="%"
            icon="ri-money-dollar-circle-line"
            variant={(liveKpis?.costVariance || 0) > 0 ? 'danger' : 'success'}
          />
          <KPICard
            title="Phiếu quá hạn"
            value={liveKpis?.overdueWorkOrders || 0}
            icon="ri-time-line"
            variant="danger"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between">
            <div className="flex flex-wrap gap-3">
              <Filter
                options={[
                  {
                    id: 'scopeType', label: 'Đối tượng', type: 'select', choices: [
                      { value: 'project', label: 'Dự án' },
                      { value: 'team', label: 'Tổ đội' },
                      { value: 'zone', label: 'Khu vực' },
                      { value: 'phase', label: 'Giai đoạn' }
                    ]
                  },
                  {
                    id: 'periodType', label: 'Chu kỳ', type: 'select', choices: [
                      { value: 'daily', label: 'Ngày' },
                      { value: 'weekly', label: 'Tuần' },
                      { value: 'monthly', label: 'Tháng' }
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-os-primary" />
              Bảng xếp hạng & Snapshot Hiệu suất
            </h3>
            <Badge variant="neutral" className="text-[10px] font-bold  tracking-widest">Dữ liệu tổng hợp</Badge>
          </div>
          <Table
            columns={[
              { key: 'scope', title: 'Đối tượng' },
              { key: 'progress', title: 'Tiến độ', align: 'center' },
              { key: 'quality', title: 'Chất lượng', align: 'center' },
              { key: 'productivity', title: 'Năng suất', align: 'center' },
              { key: 'cost', title: 'Chi phí', align: 'right' },
              { key: 'score', title: 'Điểm tổng', align: 'center' },
            ]}
            data={snapshots}
            isLoading={isLoading}
            renderCell={(item, key) => {
              switch (key) {
                case 'scope':
                  return (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-900">{item.scope_name}</span>
                      <span className="text-[10px] text-slate-400 font-bold  tracking-tighter italic">#{item.scope_id}</span>
                    </div>
                  );
                case 'progress':
                  return (
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-slate-700">{item.completion_rate}%</span>
                      <div className="w-16 h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-os-primary" style={{ width: `${item.completion_rate}%` }} />
                      </div>
                    </div>
                  );
                case 'quality':
                  return (
                    <div className="flex items-center justify-center gap-1.5">
                      <Badge variant={item.quality_score > 90 ? 'success' : 'neutral'} className="text-[9px] font-bold">
                        {item.quality_score}
                      </Badge>
                    </div>
                  );
                case 'productivity':
                  return <span className="text-[11px] font-bold text-slate-600">x{item.labor_productivity}</span>;
                case 'cost':
                  return (
                    <div className="flex flex-col items-end">
                      <span className="text-xs font-bold text-slate-900">{formatCompactCurrency(item.actual_cost)}</span>
                      <span className={`text-[9px] font-bold ${item.cost_variance_pct > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                        {item.cost_variance_pct > 0 ? '+' : ''}{item.cost_variance_pct}%
                      </span>
                    </div>
                  );
                case 'score':
                  return (
                    <div className="w-10 h-10 rounded-xl bg-os-primary/10 flex flex-col items-center justify-center border border-os-primary/20">
                      <span className="text-sm font-black text-os-primary tracking-tighter">{item.overall_score}</span>
                    </div>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>

        {/* Detailed Analysis Section (Placeholders) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-line-chart-line text-blue-500" />
              Biến động Tiến độ & Khối lượng
            </h3>
            <div className="h-64 flex items-center justify-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <span className="text-xs text-slate-400 italic">Biểu đồ tiến độ dự án (Planned vs Actual)</span>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-pie-chart-line text-emerald-500" />
              Cơ cấu Lỗi Chất lượng (QA/NCR)
            </h3>
            <div className="h-64 flex items-center justify-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <span className="text-xs text-slate-400 italic">Phân tích nguyên nhân lỗi & Rework</span>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}

const formatCompactCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value);
};
